import { Fragment, useEffect, useState } from 'react';
import { PositionsGroup } from './PositionsGroup';
import { PositionsRow } from './PositionsRow';
import { Box, Typography } from '@mui/material';
import { useSortable } from '@dnd-kit/sortable';
import { PositionsHOAIPhase } from './PositionsHOAIPhase';
import { PositionsPercent } from './PositionsPercent';
import { useConfig } from '../../../api/api';
import MoneyLabel from '../../label/MoneyLabel';
import { useTranslation } from 'react-i18next';
import Controls from '../position-modules/Controls';

export const Position = ({ props, index, item, context }) => {
  const {
    items,
    activeItem,
    projectedIndex,
    dynamicColumns,
    horizontalDelta,
    posColWidth,
    groupKey,
    collapseModules,
    imageType,
    imageApiFilter,
    descriptionVars,
    showVars,
    openAmount,
    calculateDepthFromDelta,
    onFocus,
    setItems,
    calculateNewPositions,
    onEditHOAI,
    handleDelete,
    handleNewPosition,
  } = context;

  const findPreviousAndNext = (items, activeItem, currentItem) => {
    const index = items.findIndex((el) => el.keyId == currentItem.keyId);
    const activeIndex = items.findIndex((el) => el.keyId == activeItem.keyId);

    let offset = 0;
    if (activeItem.isGroup) {
      const activeDepth = items[activeIndex].depth;
      let counter = activeIndex + 1;
      while (items[counter].depth > activeDepth) {
        counter++;
      }
      offset = counter - activeIndex;
    }

    let prevIndex;
    if (items[activeIndex].isGroup && activeIndex + offset == index) {
      prevIndex = index - offset - 1;
    } else if (index - 1 == activeIndex) {
      prevIndex = index - 2;
    } else {
      prevIndex = index - 1;
    }

    const nextIndex = index === activeIndex ? index - 1 : index;

    return [
      prevIndex < 0 ? { isGroup: false, depth: 0 } : items[prevIndex],
      nextIndex >= items.length
        ? { isGroup: false, depth: 0 }
        : items[nextIndex],
    ];
  };

  if (item.sortId === activeItem?.sortId) {
    return <tr className="h-0"></tr>;
  }

  if (item.hidden) {
    return <tr className="h-0"></tr>;
  }

  const shouldInsertPreview =
    projectedIndex === index || projectedIndex === items.length;

  return (
    <Fragment key={'' + item.keyId + 'PositionElement'}>
      {shouldInsertPreview && (
        <PreviewElement
          colSpan={dynamicColumns.length}
          depth={calculateDepthFromDelta(
            horizontalDelta,
            activeItem.depth,
            findPreviousAndNext(items, activeItem, items[index])
          )}
          posColWidth={posColWidth}
        />
      )}
      {item.isPostPosDivider ? (
        <PostPositionDivider
          item={item}
          collapseModules={collapseModules}
          dynamicColumns={dynamicColumns}
          context={context}
          showSubtotal={
            !(
              item.isPostPosDivider &&
              !items.find(
                (el) =>
                  el.isPostPosition &&
                  !el.isPostPosDivider &&
                  !el.isLastPlaceholder &&
                  !el.isSum
              ) &&
              !activeItem
            )
          }
        />
      ) : item.isLastPlaceholder ? (
        item.sortId && (
          <DropDummy
            key={item.keyId}
            item={item}
            colSpan={dynamicColumns.length}
          />
        )
      ) : item.type == 'percent' ? (
        <PositionsPercent
          props={props}
          key={'drag-' + item.keyId + item.id + '-' + 'Row'}
          collapseModules={collapseModules}
          index={index}
          items={items}
          item={item}
          columns={dynamicColumns}
          imageType={imageType}
          imageApiFilter={imageApiFilter}
          descriptionVars={descriptionVars}
          showVars={showVars}
          openAmount={openAmount}
          onFocus={(ref, key) =>
            onFocus({
              index: index,
              key,
              ref: ref,
            })
          }
          onChange={(newItems) => {
            setItems(calculateNewPositions(newItems, false, true));
          }}
          onCreate={handleNewPosition}
          onDelete={handleDelete}
          isDragging={Boolean(activeItem)}
        />
      ) : item.isGroup ? (
        <PositionsGroup
          {...props}
          key={'drag-' + item.keyId + item.id + '-' + 'Group'}
          index={index}
          items={items}
          item={item}
          columns={dynamicColumns}
          groupKey={groupKey}
          collapseModules={collapseModules}
          onFocus={(ref, key) =>
            onFocus({
              index: index,
              key,
              ref: ref,
            })
          }
          onChange={(newItems) => {
            setItems(calculateNewPositions(newItems, false, true));
          }}
          onEdit={item.editableHOAI ? onEditHOAI : null}
          onDelete={handleDelete}
          isDragging={Boolean(activeItem)}
          //toggleGroupExpand={toggleGroupExpand}
        />
      ) : item.hoaiId && item.type == 'hoaiPhase' ? (
        <PositionsHOAIPhase
          props={props}
          key={'drag-' + item.keyId + item.id + '-' + 'Row'}
          collapseModules={collapseModules}
          index={index}
          items={items}
          item={item}
          columns={dynamicColumns}
          imageType={imageType}
          imageApiFilter={imageApiFilter}
          descriptionVars={descriptionVars}
          showVars={showVars}
          openAmount={openAmount}
          onFocus={(ref, key) =>
            onFocus({
              index: index,
              key,
              ref: ref,
            })
          }
          onChange={(newItems) => {
            setItems(calculateNewPositions(newItems, false, true));
          }}
          onCreate={handleNewPosition}
          onDelete={handleDelete}
          isDragging={Boolean(activeItem)}
        />
      ) : (
        <PositionsRow
          props={props}
          key={'drag-' + item.keyId + item.id + '-' + 'Row'}
          collapseModules={collapseModules}
          index={index}
          items={items}
          item={item}
          columns={dynamicColumns}
          imageType={imageType}
          imageApiFilter={imageApiFilter}
          descriptionVars={descriptionVars}
          showVars={showVars}
          openAmount={openAmount}
          onFocus={(ref, key) =>
            onFocus({
              index: index,
              key,
              ref: ref,
            })
          }
          onChange={(newItems) => {
            setItems(calculateNewPositions(newItems, false, true));
          }}
          onCreate={handleNewPosition}
          onDelete={handleDelete}
          isDragging={Boolean(activeItem)}
        />
      )}
    </Fragment>
  );
};

const PreviewElement = ({ colSpan, depth, posColWidth }) => {
  return (
    <tr>
      <td></td>
      {Array.from({ length: depth }).map((_, i) => (
        <td key={`depth-td-${i}`} />
      ))}
      <td colSpan={colSpan}>
        <Box className="flex w-full items-center mt-[-4px] mb-[-2px]">
          <Box
            className={`bg-blue-500 rounded-full ml-[2px]`}
            sx={{
              width: posColWidth - 4 + 'px',
              height: posColWidth - 4 + 'px',
            }}
          />
          <Box className="bg-blue-500 h-[4px] w-full" />
        </Box>
      </td>
    </tr>
  );
};

const DropDummy = ({ item, colSpan }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: item.sortId, disabled: true });

  return (
    <tr
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      style={{
        transition,
        cursor: 'default',
      }}
    >
      <td colSpan={colSpan} className="h-[5px]" />
    </tr>
  );
};

const PostPositionDivider = ({
  item,
  collapseModules,
  dynamicColumns,
  context,
  showSubtotal,
}) => {
  const config = useConfig();
  const [columnCount, setColumnCount] = useState(null);

  const { t } = useTranslation();

  useEffect(() => {
    var newColumnCount = 0;
    var newMobileHiddenCount = 0;
    dynamicColumns.forEach((col) => {
      if (!col.hidden) {
        newColumnCount++;

        if (col.hideOnMobile) newMobileHiddenCount++;
      }
    });
    setColumnCount(newColumnCount);
  }, [dynamicColumns]);

  return (
    <DraggableSumElement
      item={item}
      collapseModules={collapseModules}
      colSpan={columnCount}
      text={t('Subtotal')}
      currency={config && config.currency.currencySymbol}
      context={context}
      showSubtotal={showSubtotal}
    />
  );
};

const DraggableSumElement = ({
  item,
  colSpan,
  text,
  collapseModules,
  context,
  showSubtotal,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: item.sortId, disabled: true });

  const colCorrection = collapseModules ? 1 : 2;

  const colsToAdd =
    colSpan - colCorrection - 2 > 0 ? colSpan - colCorrection - 2 : 0;

  return (
    <>
      <Controls
        hideGroups={context.hideGroups}
        hideSum={context.hideSum}
        hideServices={context.hideServices}
        hideProducts={context.hideProducts}
        hideNewPosition={context.hideNewPosition}
        hideDiscount={context.hideDiscount}
        hideHOAI={context.hideHOAI}
        hideTimeBilling={context.hideTimeBilling}
        hidePostPos={context.hidePostPositions}
        hideImport={context.hideImport}
        cols={context.cols}
        onNewGroup={context.onNewGroup}
        onNewPosition={context.onNewPosition}
        onSelectProduct={context.onSelectProduct}
        onSelectService={context.onSelectService}
        onNewDiscount={context.onNewDiscount}
        onNewPercent={context.onNewPercent}
        onNewHOAI={context.onNewHOAI}
        onNewTimeBilling={context.onNewTimeBilling}
        onImport={context.onImport}
      />
      {showSubtotal && (
        <tr
          ref={setNodeRef}
          {...listeners}
          {...attributes}
          style={{
            transition,
            cursor: 'default',
          }}
        >
          <td colSpan={colsToAdd - 2}></td>
          <td></td>
          <td className="hidden md:table-cell"></td>
          <td colSpan={2}>
            <Box className="w-full text-right">{text}</Box>
          </td>
          <td colSpan={2}>
            <Box className="w-full flex justify-end py-4">
              <Box className="w-[130px] text-right monospace pr-1">
                <Typography>
                  <MoneyLabel money={item.sumNetto ?? 0} />
                </Typography>
              </Box>
            </Box>
          </td>
        </tr>
      )}
    </>
  );
};
